/// <amd-module name="VSS/Features/PlatformUI/Components/ContributedIcon/ContributedIcon" />
import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IContributedIconDefinition } from "VSS/Platform/ContributionTypes";
import { getAssetLocation } from "VSS/Platform/Location";
import { getCurrentTheme } from "VSS/Platform/Theming";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { Icon, IIconProps } from "VSSUI/Icon";
import { Image } from "VSSUI/Image";
import { css } from "VSSUI/Util";
import { IContributedIconProps } from "./ContributedIcon.Props";

/**
 * Translates the "icon" property from a contribution to IIconProps that can be used to render the contribution's icon
 *
 * @param pageContext
 * @param contributionId
 * @param iconProperty
 */
export function contributedIconToIconProps(
    pageContext: IVssPageContext,
    contributionId: string | undefined,
    iconProperty: string | IContributedIconDefinition | undefined,
    alt?: string
): IIconProps | undefined {
    let props: IIconProps | undefined;

    if (iconProperty) {
        let iconUrl: string | undefined;
        let iconClassName: string | undefined;

        if (typeof iconProperty === "object") {
            iconUrl = iconProperty[getCurrentTheme(pageContext).isDark ? "dark" : "light"];
        } else {
            iconClassName = "non-themed";
            iconUrl = iconProperty as string;
        }

        if (iconUrl && !iconUrl.startsWith("css://")) {
            if (!iconUrl.match(/^https?:\/\//)) {
                if (contributionId) {
                    const contributionSvc = pageContext.getService<IVssContributionService>("IVssContributionService");
                    const provider = contributionSvc.getContributionProvider(contributionId);
                    if (provider) {
                        iconUrl = combineUrlPaths(provider.baseUri, iconUrl);
                    }
                }
            }

            props = {
                render: (className?: string) => <Image alt={alt} className={css(className, iconClassName, "contributed-icon-image")} src={iconUrl!} />
            };
        }
    }

    return props;
}

export function ContributedIcon(props: IContributedIconProps): JSX.Element | null {
    const className = css(props.className, "contributed-icon");

    if (props.iconProps) {
        return <Icon {...props.iconProps} className={css(className, props.iconProps.className)} />;
    }

    if (props.iconAsset) {
        const iconUrl = getAssetLocation(props.pageContext, props.iconAsset);

        if (iconUrl) {
            return <Image alt={props.alt} className={css(className, "contributed-icon-image")} src={iconUrl} />;
        }
    }

    if (props.icon) {
        const iconProps = contributedIconToIconProps(props.pageContext, props.contributionId, props.icon, props.alt);
        if (iconProps) {
            return <Icon className={className} {...iconProps} size={props.iconSize} />;
        }
    }

    if (props.iconName) {
        return <Icon className={className} iconName={props.iconName} size={props.iconSize} />;
    }

    return null;
}
